/* eslint-disable @typescript-eslint/naming-convention */
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { usePrevious } from './usePrevious'

interface UseQueryParamOptions {
  type: 'string' | 'boolean'
}

const TRUTHY = ['1', 1, 'true', true]
const FALSEY = ['0', 0, 'false', false]

const castToBoolean = (string: string | boolean | number | null): boolean => {
  if (!string) {
    return false
  }

  if (TRUTHY.includes(string)) {
    return true
  }
  if (FALSEY.includes(string)) {
    return false
  }

  return Boolean(string)
}

const deleteQueryParam = (queryParam: string) => {
  const url = new URL(window.location.href)

  url.searchParams.delete(queryParam)
  window.history.replaceState(null, '', url)
}

const getQueryParam = (queryParam: string) => {
  const searchParams = new URLSearchParams(document.location.search)

  return searchParams.get(queryParam)
}

const pushQueryParamToHistory = (
  queryParam: string,
  value: boolean | string,
) => {
  const url = new URL(window.location.href)

  url.searchParams.set(queryParam, value.toString())
  window.history.replaceState(null, '', url)
}

export const useQueryParams = <T extends boolean | string>(
  queryParamName: string,
  { type = 'string' }: UseQueryParamOptions,
): [T, Dispatch<SetStateAction<T>>] => {
  const defaultQueryParamValue = getQueryParam(queryParamName) || ''

  const [state, setState] = useState<T>(
    type === 'boolean'
      ? (castToBoolean(defaultQueryParamValue) as T)
      : (defaultQueryParamValue as T),
  )

  const prevState = usePrevious(state)

  useEffect(() => {
    if (prevState && !state) {
      deleteQueryParam(queryParamName)

      return
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (state) {
      pushQueryParamToHistory(queryParamName, state)
    }
  }, [state, prevState, queryParamName])

  return [state, setState]
}
